import React, { useEffect, useState } from "react";
import { Wrench, Clock, Phone, MessageCircle } from "lucide-react";
import { HOSPITAL } from "@/lib/i18n";

const pad = (n) => String(n).padStart(2, "0");

const Countdown = ({ target }) => {
  const [remain, setRemain] = useState(() => {
    if (!target) return null;
    return Math.max(0, new Date(target).getTime() - Date.now());
  });

  useEffect(() => {
    if (!target) return;
    const tick = () => {
      setRemain(Math.max(0, new Date(target).getTime() - Date.now()));
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [target]);

  if (!target || remain === null) return null;

  const totalSec = Math.floor(remain / 1000);
  const days = Math.floor(totalSec / 86400);
  const hours = Math.floor((totalSec % 86400) / 3600);
  const mins = Math.floor((totalSec % 3600) / 60);
  const secs = totalSec % 60;

  const isExpired = remain <= 0;

  return (
    <div
      data-testid="maintenance-countdown"
      className="mt-6 flex flex-wrap items-center justify-center gap-2 md:gap-3"
    >
      {days > 0 && (
        <div className="flex flex-col items-center px-4 py-3 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20 min-w-[72px]">
          <div className="font-mono font-bold text-3xl md:text-4xl tabular-nums">
            {days}
          </div>
          <div className="text-[10px] uppercase tracking-wider text-white/60 mt-1">
            Hari
          </div>
        </div>
      )}
      <div className="flex flex-col items-center px-4 py-3 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20 min-w-[72px]">
        <div className="font-mono font-bold text-3xl md:text-4xl tabular-nums">
          {pad(hours)}
        </div>
        <div className="text-[10px] uppercase tracking-wider text-white/60 mt-1">
          Jam
        </div>
      </div>
      <div className="flex flex-col items-center px-4 py-3 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20 min-w-[72px]">
        <div className="font-mono font-bold text-3xl md:text-4xl tabular-nums">
          {pad(mins)}
        </div>
        <div className="text-[10px] uppercase tracking-wider text-white/60 mt-1">
          Menit
        </div>
      </div>
      <div className="flex flex-col items-center px-4 py-3 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20 min-w-[72px]">
        <div className="font-mono font-bold text-3xl md:text-4xl tabular-nums">
          {pad(secs)}
        </div>
        <div className="text-[10px] uppercase tracking-wider text-white/60 mt-1">
          Detik
        </div>
      </div>
      {isExpired && (
        <div className="w-full text-center text-amber-300 text-sm mt-2">
          Estimasi telah lewat — silakan refresh halaman.
        </div>
      )}
    </div>
  );
};

const MaintenancePage = ({ state }) => {
  const {
    message = "Website sedang dalam pemeliharaan. Silakan kembali lagi nanti.",
    estimated_until,
    contact_phone = "+6282241761118",
  } = state || {};

  const waHref = `https://wa.me/${(contact_phone || "").replace(/\D+/g, "")}?text=${encodeURIComponent(
    "Halo, saya ingin informasi selama website RS Bhayangkara Tarakan dalam pemeliharaan."
  )}`;

  const estimatedText = estimated_until
    ? new Date(estimated_until).toLocaleString("id-ID", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : null;

  return (
    <div
      data-testid="maintenance-page"
      className="min-h-screen relative overflow-hidden bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 text-white flex items-center justify-center px-6 py-12"
    >
      {/* Decorative grid */}
      <div
        className="absolute inset-0 opacity-[0.06] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }}
      />
      {/* Glow accent */}
      <div className="absolute -top-40 -right-40 h-96 w-96 rounded-full bg-blue-600/30 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-40 -left-40 h-96 w-96 rounded-full bg-cyan-500/20 blur-3xl pointer-events-none" />

      <div className="relative max-w-2xl w-full text-center">
        {/* Logo + brand */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <img
            src={HOSPITAL.logo}
            alt="RS Bhayangkara Tarakan"
            className="h-14 w-14 rounded-2xl object-cover ring-2 ring-white/10"
          />
          <div className="text-left">
            <div className="font-heading font-bold text-lg">
              RS Bhayangkara Tarakan
            </div>
            <div className="text-xs text-blue-200/80">
              Polda Kalimantan Utara
            </div>
          </div>
        </div>

        {/* Icon badge */}
        <div className="inline-flex items-center justify-center h-20 w-20 rounded-3xl bg-amber-500/20 border border-amber-400/40 mb-6">
          <Wrench className="h-10 w-10 text-amber-300 animate-pulse" />
        </div>

        {/* Title */}
        <h1
          data-testid="maintenance-title"
          className="font-heading font-bold text-4xl md:text-5xl leading-tight mb-4"
        >
          Sedang dalam Pemeliharaan
        </h1>

        {/* Message */}
        <p
          data-testid="maintenance-message"
          className="text-base md:text-lg text-blue-100/80 max-w-xl mx-auto leading-relaxed whitespace-pre-wrap"
        >
          {message}
        </p>

        {/* Countdown */}
        {estimated_until && <Countdown target={estimated_until} />}

        {/* Estimated ETA text */}
        {estimatedText && (
          <div
            data-testid="maintenance-eta"
            className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-sm text-blue-100"
          >
            <Clock className="h-4 w-4" />
            Estimasi kembali: <b>{estimatedText}</b>
          </div>
        )}

        {/* Contact CTA */}
        <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
          <a
            href={waHref}
            target="_blank"
            rel="noreferrer"
            data-testid="maintenance-whatsapp"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold bg-emerald-500 hover:bg-emerald-600 text-white transition shadow-lg shadow-emerald-500/20"
          >
            <MessageCircle className="h-4 w-4" />
            Hubungi via WhatsApp
          </a>
          <a
            href={`tel:${contact_phone}`}
            data-testid="maintenance-phone"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold bg-white/10 hover:bg-white/20 border border-white/20 text-white transition"
          >
            <Phone className="h-4 w-4" />
            {contact_phone}
          </a>
        </div>

        {/* IGD note */}
        <div className="mt-10 pt-6 border-t border-white/10 text-xs text-blue-200/60">
          <b>IGD 24 Jam</b> tetap beroperasi normal. Untuk keadaan darurat medis
          silakan langsung ke rumah sakit.
        </div>
      </div>
    </div>
  );
};

export default MaintenancePage;
